import React, { useEffect, useRef, useState } from "react";
import { Step, StepOption } from "../../types/wizard";
import { IMAGE_SERVER } from "../../config";
import { saveEmblemData, setStepCallback, startFromProductType, wizardStore } from "../../stores/wizard";
import { useSnapshot } from "valtio";
import useOutsideClick from "../../hooks/useOutsideClick";
import Loading from "./Loading";
import { Steps } from "../../constants";
import "./../QuickView/QuickView.css";

interface DropdownProps {
  step: Step;
}

const ImageWithSkeleton: React.FC<{ src: string; alt: string; className?: string }> = ({
  src,
  alt,
  className,
}) => {
  const [isLoading, setIsLoading] = useState(true);

  return (
    <div className={`relative ${className}`} style={{ width: "2rem", height: "2rem" }}>
      {isLoading && (
        <div className="rounded-full object-cover bg-slate-200 w-8 h-8 animate-pulse" />
      )}
      <img
        src={src}
        alt={alt}
        className={`rounded object-cover w-8 h-8 ${isLoading ? "opacity-0" : "opacity-100"
          }`}
        onLoad={() => setIsLoading(false)}
      />
    </div>
  );
};

const Selector: React.FC<DropdownProps> = ({ step }) => {
  const wizSnap = useSnapshot(wizardStore);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<StepOption | null>(null);
  const [searchText, setSearchText] = useState<string>("");
  const selectRef = useRef(null);

  useOutsideClick([selectRef], () => {
    setIsOpen(false);
  });

  useEffect(() => {
    if (!step?.id) return;
    const savedOption = wizSnap.emblemData[step.id] || null;
    setSelectedOption(savedOption);
  }, [wizSnap.emblemData, step?.id]);

  const handleSelect = (option: StepOption) => {
    if (!step?.id) return;

    setSearchText("");
    setSelectedOption(option);

    if (step.id === Steps.ProductType) {
      // Remove saved data after selecting a new Product Type
      startFromProductType(step.id, option);
    } else {
      saveEmblemData(step.id, option);
    }

    const updatedStepContent = {
      ...wizSnap.emblemData[step.id],
      ...option,
    };

    const setCallback = setStepCallback[step.id];
    if (setCallback) {
      setCallback(updatedStepContent, step.id);
    } else {
      console.warn(`No callback defined for step: ${step.id}`);
    }

    setIsOpen(false);
  };

  // Search filter
  const filteredOptions = step.options?.filter((option) =>
    option.name.toLowerCase().includes(searchText.toLowerCase())
  );

  const getBorderColor = (htmlColor: string) => {
    return !!(htmlColor && htmlColor === "#FFFFFF") ? '1px solid #a7acaf' : 'none';
  }

  if (wizSnap.loading)
    return <Loading />

  return (
    <div className="w-full mt-5" ref={selectRef}>
      <label className="block uppercase font-bold mb-2.5">{step.title}</label>
      {
        step.disclaimer && <div className="flex flex-row pb-4 w-full">
          <span dangerouslySetInnerHTML={{ __html: step.disclaimer.message }}></span>
        </div>
      }
      <div
        className="p-2.5 cursor-pointer relative rounded bg-white"
        onClick={() => setIsOpen(!isOpen)}
        style={{ border: "1px solid #ccc" }}
      >
        {selectedOption ? (
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            {selectedOption.thumbnailImage?.url ? (
              <img
                src={`${IMAGE_SERVER}${selectedOption.thumbnailImage?.url}`}
                alt={selectedOption.name}
                className="w-24 h-24 object-cover rounded w-8 h-8"
              />
            ) :
              selectedOption?.htmlColor && <div
                className="w-24 h-24 object-cover rounded w-8 h-8"
                style={{ background: selectedOption.htmlColor, border: getBorderColor(selectedOption.htmlColor) }}>
              </div>
            }
            <span>{selectedOption.name}</span>
          </div>
        ) : (
          <div className="flex flex-row justify-between items-center">
            <span style={{ display: "flex" }}>-- Select a Value --</span>
            <div className="arrow"></div>
          </div>
        )}
      </div>

      {isOpen && (
        <div className="relative w-full">
          {/* Seach input */}
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search"
            className="p-2 w-full border-b-2 border-gray-300 rounded outline-none"
            style={{
              border: "1px solid #ccc"
            }}
          />
          <ul
            className="m-0 p-0 rounded bg-white overflow-y-auto"
            style={{
              listStyle: "none",
              border: "1px solid #ccc",
              maxHeight: "200px",
              width: "inherit",
              zIndex: 1,
              display: filteredOptions?.length === 0 ? 'none' : 'inherit'
            }}
          >
            {filteredOptions?.map((option) => (
              <li
                key={option.id}
                onClick={() => handleSelect(option)}
                className="flex items-center p-2 cursor-pointer"
                style={{
                  gap: "10px",
                  borderBottom: "1px solid #eee",
                  transition: "background-color 0.2s",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "#f5f5f5")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "transparent")
                }
              >
                {option.thumbnailImage?.url ? (
                  <ImageWithSkeleton
                    src={`${IMAGE_SERVER}${option.thumbnailImage.url}`}
                    alt={option.name}
                    className="w-8 h-8"
                  />
                ) :
                  option?.htmlColor &&
                  <div
                    className="w-24 h-24 object-cover rounded w-8 h-8"
                    style={{ background: option.htmlColor, border: getBorderColor(option.htmlColor) }}>
                  </div>
                }
                <span>{option.name}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default Selector;
